import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DocumentTypes, EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowLeftRightIcon,
  ArrowRight02Icon,
  EyeIcon,
  FolderIcon,
  NewOfficeIcon,
  SearchingIcon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { useTranslations } from 'use-intl';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/app/ui/components/main';
import { H1, H2, H3 } from '@/app/ui/components/heading';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { CreateChangeRequest } from '@/app/components/features/supplements/changeAssessments/CreateChangeRequest';
import { Frame, FramePanel } from '@/app/ui/components/frame';
import { SummaryIcon } from '@/app/components/shared/EmptyIcons';
import { useGetChangeAssessmentList } from '@/app/hooks/queries/useChangeAssessments';
import { Button } from '@/app/ui/components/button';
import { createChangeRequestOpenAtom } from '@/app/components/features/supplements/changeAssessments/store';
import { useSetAtom } from 'jotai';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { cn } from '@/app/utils/helpers';
import { Spinner } from '@/app/ui/components/spinner';
import { SummarizationNamespaces } from '@/app/utils/constants';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { LinkProps } from '@tanstack/react-router';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/contract-management/$supplementId/_layout/(supplement)/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractManagement');
  const tHome = useTranslations('pages.contractHome');
  const navigate = useNavigate();
  const { contractId, projectId, supplementId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: documents } = useEntityDocuments(supplementId, DocumentTypes.SUPPLEMENT);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={ArrowLeftRightIcon} />
                {t('titleLong')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={supplementId}
          summaryType={SummarizationNamespaces.SUPPLEMENT}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/contracts/$contractId/contract-management/$supplementId/files',
              params: { projectId, contractId, supplementId: evaluation.supplementPackageId },
            })
          }
        />
        <MainHeader>
          <H1>{t('titleLong')}</H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            {evaluation.companyName && (
              <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                <HugeiconsIcon icon={NewOfficeIcon} />
                {evaluation.companyName}
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/contracts/$contractId/contract-management/$supplementId/files"
                params={{ projectId, contractId, supplementId: evaluation.supplementPackageId }}
              >
                <HugeiconsIcon icon={FolderIcon} />
                {tHome('nav.files')}{' '}
                {documents?.length === 0 ? null : `(${documents?.length || 0})`}
              </MainNavLink>
            </MainNavItem>
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t('section1Title')}</H2>
          <MainFrames>
            <ChangeAnalysisFrame
              id={supplementId}
              to="/project/$projectId/contracts/$contractId/contract-management/$supplementId/change-analysis"
              params={{ projectId, contractId, supplementId: evaluation.supplementPackageId }}
            />
          </MainFrames>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}

interface ChangeAnalysisFrameProps extends LinkProps {
  id: string;
  className?: string;
}

function ChangeAnalysisFrame({ id, className, ...props }: ChangeAnalysisFrameProps) {
  const t = useTranslations('features.changeAnalysis');
  const navigate = useNavigate();
  const setCreateOpen = useSetAtom(createChangeRequestOpenAtom);
  const { data: assessments, isLoading } = useGetChangeAssessmentList({
    contractSupplementPackageId: id,
  });

  const hasAssessments = assessments && assessments.length > 0;

  return (
    <>
      <Frame
        className={cn(
          'data-has-data:cursor-pointer data-has-data:hover:shadow-md/4 transition-shadow',
          className,
        )}
        data-has-data={hasAssessments ? '' : undefined}
        {...props}
        onClick={() => {
          if (hasAssessments) return navigate(props);
        }}
      >
        <FramePanel className="h-38 grid place-items-center">
          {isLoading ? (
            <Spinner kind="inset" className="translate-y-0" />
          ) : hasAssessments ? (
            <p>
              {t.rich('found', {
                count: assessments.length,
                strong: (chunks) => (
                  <strong className="block font-medium text-4xl text-center">{chunks}</strong>
                ),
              })}
            </p>
          ) : (
            <SummaryIcon className="size-25" />
          )}
        </FramePanel>
        <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
          <div>
            <H3>{t('title')}</H3>
            <p className="text-muted mt-0.5 text-sm">{t('description')}</p>
          </div>
          {hasAssessments ? (
            <HugeiconsIcon
              icon={ArrowRight02Icon}
              className="invisible group-hover:visible ml-auto max-md:hidden"
            />
          ) : null}
          <div
            data-has-data={hasAssessments ? '' : undefined}
            className="data-has-data:md:hidden md:absolute md:grid rounded-lg md:opacity-0 group-hover:opacity-100 md:place-items-center md:inset-0 md:bg-black/7"
          >
            <Button
              className="max-md:w-full max-md:mt-2 backdrop-blur-lg"
              size="sm"
              kind="with-icon"
              variant={hasAssessments ? 'tertiary-2' : 'secondary'}
              onClick={() => {
                if (hasAssessments) return navigate(props);
                setCreateOpen(true);
              }}
            >
              <HugeiconsIcon icon={hasAssessments ? EyeIcon : SearchingIcon} />
              {hasAssessments ? t('buttons.view') : t('buttons.new')}
            </Button>
          </div>
        </div>
      </Frame>
      <CreateChangeRequest />
    </>
  );
}
